
import { Home, Calendar, Target, ShoppingCart, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useWorkout } from "@/contexts/WorkoutContext";
import { useAuth } from "@/hooks/useAuth";
import frequentHealthIcon from "@/assets/frequent-health-icon.jpeg";

type NavItem = {
  icon: React.ElementType;
  label: string;
  href: string;
  isActive?: boolean;
};

export function DesktopSidebar({
  activeTab = "home"
}: {
  activeTab?: string;
}) {
  const { isWorkoutActive, returnToWorkout } = useWorkout();
  const { profile, isClient } = useAuth();
  
  // Block navigation if legal approval is needed
  const isBlocked = isClient && profile?.legal_document_approval_needed;
  const legalRedirectTarget = "/client-profile?tab=legal";
  
  const navItems: NavItem[] = [{
    icon: Home,
    label: "Home",
    href: "/client-portal",
    isActive: activeTab === "home"
  }, {
    icon: Calendar,
    label: "Plan",
    href: "/client-plan",
    isActive: activeTab === "plan"
  }, {
    icon: Target,
    label: "Explore",
    href: "/explore",
    isActive: activeTab === "explore"
  }, {
    icon: MessageSquare,
    label: "Chat",
    href: "/chat",
    isActive: activeTab === "chat"
  }];
  
  return (
    <div className="hidden md:flex flex-col h-screen bg-sidebar border-r border-sidebar-border w-16 fixed left-0 top-0 z-40">
      <div className="flex items-center justify-center h-16 border-b border-sidebar-border">
        <Link
          to={isBlocked ? legalRedirectTarget : "/client-portal"}
          title={isBlocked ? "Legal document approval required" : "Home"}
          className={cn("transition-opacity", isBlocked && "opacity-70 hover:opacity-100")}
        >
          <img
            src={frequentHealthIcon}
            alt="Frequent Health"
            className="w-10 h-10 object-contain"
          />
        </Link>
      </div>
      
      <div className="flex flex-col items-center py-6 space-y-8">
        {navItems.map((item) => (
          <NavItem key={item.label} item={item} isBlocked={isBlocked} legalRedirectTarget={legalRedirectTarget} />
        ))}
        
        {/* Workout button */}
        <div className="flex flex-col items-center">
          {isBlocked ? (
            <Link
              to={legalRedirectTarget}
              className={cn(
                "w-12 h-12 rounded-full flex flex-col items-center justify-center",
                "bg-gray-600 text-gray-400 opacity-70 hover:opacity-100 transition-opacity"
              )}
              title="Legal document approval required"
            >
              <span className="text-xl">🏋️</span>
            </Link>
          ) : (
            <Link 
              to={isWorkoutActive ? "#" : "/start-workout"}
              onClick={isWorkoutActive ? returnToWorkout : undefined}
              className={cn(
                "w-12 h-12 rounded-full flex flex-col items-center justify-center",
                "gradient-primary text-black shadow-neon-glow",
                "transition-transform hover:scale-110",
                isWorkoutActive ? "animate-pulse-glow" : ""
              )}
            >
              <span className="text-xl">🏋️</span>
            </Link>
          )}
          <span className="text-xs mt-1 text-sidebar-foreground font-medium">Workout</span>
        </div>
      </div>
    </div>
  );
}

function NavItem({ item, isBlocked, legalRedirectTarget }: { item: NavItem; isBlocked?: boolean; legalRedirectTarget: string }) {
  const destination = isBlocked ? legalRedirectTarget : item.href;
  return (
    <Link 
      to={destination} 
      className={cn(
        "flex flex-col items-center group",
        isBlocked && "opacity-70 hover:opacity-100"
      )}
      title={isBlocked ? "Legal document approval required" : undefined}
    >
      <div className={cn(
        "w-12 h-12 rounded-lg flex items-center justify-center",
        isBlocked ? "text-sidebar-foreground" : "transition-colors group-hover:bg-sidebar-accent",
        item.isActive && !isBlocked ? "text-primary" : "text-sidebar-foreground",
      )}>
        <item.icon size={24} />
      </div>
      <span className={cn(
        "text-xs mt-1 font-medium",
        item.isActive && !isBlocked ? "text-primary" : "text-sidebar-foreground"
      )}>
        {item.label}
      </span>
    </Link>
  );
}
